import type { DesignModuleSpecJson } from '../data/contracts';
import { decisionSha, journalOf, RECAPTURE, type Decision } from './decisionJournal';

/** E2E-FIX1 (J, DIAG-E2E-FIN cause 4) : un acquis non prouvé est une page adoptée en entier (décision « node.replace » à la racine)
 * dont l'origine est indisponible (import purgé) ou héritée (« legacy ») : rien ne prouve ses liaisons, et « Relier » ne repropose
 * jamais une région déjà liée. Le geste du Maker « Repartir de la capture » retire, pour les pages choisies, le gabarit adopté et
 * les décisions qui portent sur ses nœuds ; « Compiler » refait la page depuis sa capture, puis « Relier » la mesure.
 * La décision « page.recapture » est journalisée : le rejeu du service écarte alors, sur ces pages, les décisions des nœuds et des actions
 * de l'ancien gabarit. Les formulaires, l'écran et le modèle restent. Générique : aucune page nommée. */
export type UnprovenPage = { page: string; origin: 'unavailable' | 'legacy'; measured: number };
type Page = { key: string; screen?: { blocks?: { template?: unknown }[] } };
const ref = (entry: Decision) => (entry.prototypeRef ?? {}) as { unavailable?: boolean; measurements?: unknown[] };

export function unprovenPages(spec: DesignModuleSpecJson): UnprovenPage[] {
  const pages = new Set(((spec.pages ?? []) as Page[]).filter(page => page.screen?.blocks?.some(block => block.template)).map(page => page.key));
  const out = new Map<string, UnprovenPage>();
  for (const entry of journalOf(spec).entries) {
    if (entry.nature !== 'node.replace' || (entry.path ?? []).length || !pages.has(entry.page)) continue;
    const unavailable = ref(entry).unavailable === true, legacy = entry.source === 'legacy';
    if (!unavailable && !legacy) { out.delete(entry.page); continue; }
    out.set(entry.page, { page: entry.page, origin: unavailable ? 'unavailable' : 'legacy', measured: (ref(entry).measurements ?? []).length });
  }
  return [...out.values()];
}

/** GAP-31 (passe réelle n° 3 de Finances) : la clé et l'empreinte d'une décision suivent le schéma (SHA-256, ^[a-f0-9]{64}$). Mêmes
 * valeurs que la guérison serveur (ModuleSpecDecisionJournalRepair) : SHA-256 de « page.recapture:<page> » et de « page.recapture ». */
export const recaptureKey = (page: string) => decisionSha(RECAPTURE + ':' + page);
export const recaptureFingerprint = () => decisionSha(RECAPTURE);
const SHA = /^[a-f0-9]{64}$/;
/** Guérison idempotente des décisions « Repartir de la capture » écrites à l'ancien format (« page.recapture:<page> »). */
export async function healRecaptureDecisions(spec: DesignModuleSpecJson): Promise<DesignModuleSpecJson> {
  if (!spec.decisionJournal) return spec;
  const journal = journalOf(spec);
  if (!journal.entries.some(entry => entry.nature === RECAPTURE && (!SHA.test(entry.key) || !SHA.test(entry.fingerprint)))) return spec;
  const fingerprint = await recaptureFingerprint();
  const entries = await Promise.all(journal.entries.map(async entry => entry.nature !== RECAPTURE || SHA.test(entry.key) && SHA.test(entry.fingerprint) ? entry
    : { ...entry, key: SHA.test(entry.key) ? entry.key : await recaptureKey(entry.page), fingerprint }));
  return { ...spec, decisionJournal: { ...journal, entries } };
}

/** Le geste « Repartir de la capture » : un nouveau brouillon, jamais une écriture ; le Maker l'enregistre (commande « save-draft »). */
export async function restartFromCapture(spec: DesignModuleSpecJson, pages: readonly string[]): Promise<DesignModuleSpecJson> {
  const chosen = new Set(pages), next = structuredClone(spec);
  for (const page of (next.pages ?? []) as Page[]) if (chosen.has(page.key) && page.screen?.blocks) page.screen.blocks = page.screen.blocks.filter(block => !block.template);
  if (next.decisionJournal) {
    const journal = journalOf(next);
    journal.entries = journal.entries.filter(entry => !(chosen.has(entry.page) && (entry.nature.startsWith('node.') || entry.nature === 'page.actions' || entry.nature === RECAPTURE)));
    next.decisionJournal = journal;
  }
  // The decision itself is journaled : the replay of the service then leaves the former template's decisions aside on these pages.
  const journal = journalOf(next);
  const fingerprint = await recaptureFingerprint();
  for (const page of [...chosen].sort()) journal.entries.push({ key: await recaptureKey(page), page, path: [], nature: RECAPTURE, fingerprint, prototypeRef: { page }, value: { by: 'maker' }, source: 'maker' });
  next.decisionJournal = journal;
  return healRecaptureDecisions(next);
}
